import React from 'react';
import Typography from 'carbon-react/lib/components/typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Pill from 'carbon-react/lib/components/pill';

import { Organisation } from '@futrli-sdk/organisations';
import { Integration } from '@futrli-sdk/app-store';
import { getTimestampRelativeFormat } from '@futrli/localisation';

import * as styles from './sync-data-card.scss';
import { SyncControlDatasourceState } from '../../types';
import { SyncDataCardDivider } from './sync-data-card-divider';
import { SyncContentLoader } from '../sync-content-loader/sync-content-loader';
import { SyncDataOrgDetails } from './sync-data-org-details';

interface Props {
  organisation: Organisation | null;
  integration?: Integration;
  datasourceState: SyncControlDatasourceState;
  syncTimestamp: string;
  isLoading: boolean;
  unreconciledTransactionsCount?: number | null;
}

const StatusPill = ({
  datasourceState,
  unreconciledTransactionsCount
}: {
  datasourceState: SyncControlDatasourceState;
  unreconciledTransactionsCount?: number | null;
}) => {
  const intl = useIntl();

  // If the sync is complete but there are unreconciled transactions, we show a "Partial sync" status.
  if (
    datasourceState === SyncControlDatasourceState.SYNC_COMPLETE &&
    typeof unreconciledTransactionsCount === 'number' &&
    unreconciledTransactionsCount > 0
  ) {
    return (
      <Pill pillRole="status" size="M" colorVariant="information" fill>
        {intl.formatMessage({ id: 'common.partial_sync', defaultMessage: 'Partial sync' })}
      </Pill>
    );
  }

  if (datasourceState === SyncControlDatasourceState.SYNC_COMPLETE) {
    return (
      <Pill pillRole="status" size="M" colorVariant="positive" fill>
        {intl.formatMessage({ id: 'common.synced', defaultMessage: 'Synced' })}
      </Pill>
    );
  }

  if (datasourceState === SyncControlDatasourceState.SYNC_FAILED) {
    return (
      <Pill pillRole="status" size="M" colorVariant="negative" fill>
        {intl.formatMessage({ id: 'common.sync_failed', defaultMessage: 'Failed' })}
      </Pill>
    );
  }

  return null;
};

const LastAttemptLabel = ({
  datasourceState,
  unreconciledTransactionsCount
}: {
  datasourceState: SyncControlDatasourceState;
  unreconciledTransactionsCount?: number | null;
}) => {
  // If the sync has failed or there are unreconciled transactions, we show "Last attempt" to indicate that the last sync was not fully successful.
  if (
    datasourceState === SyncControlDatasourceState.SYNC_FAILED ||
    (typeof unreconciledTransactionsCount === 'number' && unreconciledTransactionsCount > 0)
  ) {
    return (
      <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
        <FormattedMessage id="common.sync.last_attempt" defaultMessage="Last attempt" />
      </Typography>
    );
  }

  return (
    <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
      <FormattedMessage id="common.last_synced" defaultMessage="Last synced" />
    </Typography>
  );
};

export const SyncDataCard = ({
  organisation,
  integration,
  datasourceState,
  syncTimestamp,
  isLoading,
  unreconciledTransactionsCount
}: Props) => (
  <div>
    <SyncDataOrgDetails organisation={organisation} integration={integration} />
    <SyncDataCardDivider />
    <div className={styles.syncStatusRow}>
      <div>
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.sync_status" defaultMessage="Sync status" />
        </Typography>
        {isLoading ? (
          <SyncContentLoader width={64} height={16} />
        ) : (
          <StatusPill datasourceState={datasourceState} unreconciledTransactionsCount={unreconciledTransactionsCount} />
        )}
      </div>
      <div>
        <LastAttemptLabel
          datasourceState={datasourceState}
          unreconciledTransactionsCount={unreconciledTransactionsCount}
        />
        {isLoading ? (
          <SyncContentLoader width={64} height={16} />
        ) : (
          <Typography variant="p" mb="0" fontWeight="500">
            {getTimestampRelativeFormat(syncTimestamp, Date.now(), 1)}
          </Typography>
        )}
      </div>
    </div>
    <SyncDataCardDivider />
  </div>
);
